import { Client, cacheExchange, fetchExchange, makeOperation } from '@urql/core'
import { env } from 'environments'
import { authExchange } from '@urql/exchange-auth'
import { Session } from 'next-auth'

// Session and hasuraUrl are passed as props to the function to ensure that we use the client when necceasary and if no client then we use default hasura url
type BaseUserUrqlClientArgs = {
  /** An property containing the user's session. */
  session: Session | null | undefined
  graphQLEndpointOverride?: string
}

const envToGqlClient: Record<string, Client> = {}

export function getBaseUserUrqlClient(args?: BaseUserUrqlClientArgs): Client {
  const { session, graphQLEndpointOverride } = args ?? {}

  const hasuraEndpoint = (graphQLEndpointOverride ?? env.HASURA_GRAPHQL_ENDPOINT) ? env.HASURA_GRAPHQL_ENDPOINT : null

  const key = `${hasuraEndpoint}-${session?.address ?? 'public'}`

  if (!hasuraEndpoint) {
    throw new Error('No hasura endpoint provided')
  }

  if (envToGqlClient[key]) {
    return envToGqlClient[key]
  }

  const client = new Client({
    url: hasuraEndpoint as string,
    exchanges: [
      cacheExchange,
      authExchange(async (_utils) => {
        return {
          addAuthToOperation(operation) {
            if (!session) {
              return operation
            }
            const { jwt } = session
            const fetchOptions =
              typeof operation.context.fetchOptions === 'function'
                ? operation.context.fetchOptions()
                : operation.context.fetchOptions || {}
            return makeOperation(operation.kind, operation, {
              ...operation.context,
              fetchOptions: {
                ...fetchOptions,
                headers: {
                  ...fetchOptions.headers,
                  'x-hasura-role': 'base_user',
                  Authorization: `Bearer ${jwt}`
                }
              }
            })
          },
          async refreshAuth() {
            return
          },
          didAuthError(error) {
            return error.graphQLErrors.some(
              (e) => e.extensions?.code === 'FORBIDDEN' || e.extensions?.code === 'invalid-jwt'
            )
          }
        }
      }),
      fetchExchange
    ]
  })

  envToGqlClient[key] = client

  return envToGqlClient[key]
}
